import { useEffect, useMemo, useRef, useState } from 'react';
import { chaseRestartAvailable, chaseRestartCommand, isChaseRestartKey } from './chaseRestart';
import { isModeRulesKey, modeRulesAvailable, modeRulesCommand } from './modeRules';
import type { Unsubscribe } from 'firebase/firestore';
import PairingScreen from './PairingScreen';
import {
  callableErrorCode,
  createReceiver,
  resumeReceiver,
  type TvReceiverConnection,
} from './tvSession';
import {
  clearStoredDevice,
  deviceFromIssue,
  loadStoredDevice,
  resumeBackoffMs,
  resumeFailureAction,
  storeDevice,
} from './tvDevice';
import type { TvPuttEvent, TvSessionDocument, TvSnapshot } from './protocol';
import { controllerFresh } from './presentation';
import { renderPresentation, useLiveNotice, useSessionTally, useSettledEventId } from './ReceiverPresentation';
import { diagState } from './diagState';
import { withPreviewCourse, demoFixture, footballPlayScript, baseballHomeRunScript } from './demoFixtures';
import { pageStockScenery, resolveSceneCourse } from './courses/phoneScene';
import { preloadBiome } from './scenePreload';
import { detectStageGraphics } from './quality';
import { forgetRequested, searchWithoutForget, showForgetAction } from './forgetTv';
import './styles.css';
import { shareStructure } from './structuralShare';
import { tvSfx } from './sfx';

/**
 * Where the page is in its lifecycle (TV_REVIVAL_PLAN.md §1).
 *
 * - `opening`: no session yet (first load, or a pairing session that expired
 *   before anyone paired).
 * - `pairing`: a fresh session is showing its code.
 * - `live`: a session bound to this TV's device is open.
 * - `reconnecting`: the session closed or expired; the page resumes with its
 *   stored token after a backoff and keeps the last frame meanwhile.
 */
type Phase = 'opening' | 'pairing' | 'live' | 'reconnecting';

/**
 * A passive TV receiver should not dim halfway through a practice session.
 * Browsers automatically release this lock when the tab is hidden, so it is
 * reacquired whenever the display becomes visible again.
 */
/** A putt this recent keeps the screen awake between putts. */
const AWAKE_AFTER_PUTT_MS = 10 * 60_000;

/**
 * Whether the screen should be held awake: a live session whose phone is in
 * a putting state, or a putt within the last ten minutes. Menus, pairing and
 * a quiet phone let the TV's own sleep apply (Cole, 2026-10-06, the Apple TV
 * "stays on forever"; the page follows the same rule). The phone's 45-second
 * pulses alone never count as activity.
 */
export function shouldHoldScreenAwake(
  phase: string,
  snapshot: TvSnapshot | null,
  event: TvPuttEvent | null,
  nowMs: number,
): boolean {
  if (phase !== 'live') return false;
  if (snapshot?.sleep === 'putting') return true;
  const puttAtMs = event?.createdAtMs;
  return typeof puttAtMs === 'number' && nowMs - puttAtMs < AWAKE_AFTER_PUTT_MS;
}

function useScreenWakeLock(hold: boolean) {
  useEffect(() => {
    if (!hold) return;
    const wakeLockApi = 'wakeLock' in navigator ? navigator.wakeLock : null;
    if (!wakeLockApi) return;

    let cancelled = false;
    let sentinel: WakeLockSentinel | null = null;
    const acquire = async () => {
      if (cancelled || document.visibilityState !== 'visible' || sentinel) return;
      try {
        sentinel = await wakeLockApi.request('screen');
      } catch {
        // Wake Lock is an enhancement. Pairing and rendering continue when a
        // browser or power policy declines it.
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        sentinel = null;
        void acquire();
      }
    };

    void acquire();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      void sentinel?.release().catch(() => undefined);
      sentinel = null;
    };
  }, [hold]);
}

export default function App() {
  // A data-free graphics fixture for checking the hosted bundle without a
  // phone. It never creates or joins a Firebase session.
  const demoParams = new URLSearchParams(window.location.search);
  const devDemo = demoParams.has('demo');
  const demoAddress = demoParams.has('address');
  const demoStale = demoParams.has('stale');
  const demoPairing = demoParams.get('demo') === 'pairing';
  const demoHome = demoParams.has('home');
  const demoMode = demoParams.get('demo');
  // Built once: a fixture that stamps a clock (a notice's expiresAtMs) must
  // not be re-stamped by the very re-render its expiry causes.
  // ?demo=footballplay: a scripted Football snap (footballPlayScript), stepping
  // on each step's hold so the transitions can be watched.
  // ?demo=baseballhr: a home run swing with the fielding state at the old
  // phone's 6.5 s settle (baseballHomeRunScript).
  const playScript = useMemo(
    () => (demoMode === 'footballplay' ? footballPlayScript() : demoMode === 'baseballhr' ? baseballHomeRunScript() : null),
    [demoMode],
  );
  const [playStep, setPlayStep] = useState(0);
  useEffect(() => {
    if (!playScript) return undefined;
    const timer = setTimeout(() => setPlayStep((step) => (step + 1) % playScript.length), playScript[playStep].holdMs);
    return () => clearTimeout(timer);
  }, [playScript, playStep]);
  const scriptedDemo = useMemo(
    () => (playScript ? { snapshot: playScript[playStep].snapshot, event: playScript[playStep].event } : null),
    [playScript, playStep],
  );
  const fixedDemo = useMemo(
    () => (devDemo && !demoPairing && !playScript ? withPreviewCourse(demoFixture(demoMode, demoAddress, demoHome), demoParams.get('course')) : null),
    [devDemo, demoPairing, demoMode, demoAddress, demoHome, playScript],
  );
  const demo = scriptedDemo ?? fixedDemo;

  const [phase, setPhase] = useState<Phase>('opening');
  const [pairingCode, setPairingCode] = useState('');
  const [ownerHandle, setOwnerHandle] = useState<string | null>(null);
  const [hasStoredDevice, setHasStoredDevice] = useState(() => {
    // `?forget` (forgetTv.ts): forget the stored device and pair from a
    // fresh code. The parameter comes off the address at once so a reload
    // after the new pairing keeps the new device.
    if (!devDemo && forgetRequested(window.location.search)) {
      clearStoredDevice();
      try {
        const { pathname, hash } = window.location;
        window.history.replaceState(window.history.state, '', `${pathname}${searchWithoutForget(window.location.search)}${hash}`);
      } catch {
        // The device is forgotten either way; only the address keeps the flag.
      }
    }
    return loadStoredDevice() !== null;
  });
  const [snapshot, setSnapshot] = useState<TvSnapshot | null>(null);
  const [event, setEvent] = useState<TvPuttEvent | null>(null);
  const [tvLinkFresh, setTvLinkFresh] = useState(!demoStale);
  const settledEventId = useSettledEventId(event);
  const { tally, reset: resetTally } = useSessionTally(event, snapshot?.mode ?? null);
  const connectionRef = useRef<TvReceiverConnection | null>(null);
  // Sim 9 chase RESTART from the TV (chaseRestart.ts): R or a remote's play/pause.
  const snapshotRef = useRef<TvSnapshot | null>(null);
  snapshotRef.current = snapshot;
  useEffect(() => {
    const onKey = (keyEvent: KeyboardEvent) => {
      if (keyEvent.repeat) return;
      const connection = connectionRef.current;
      if (!connection) return;
      if (isChaseRestartKey(keyEvent.key) && chaseRestartAvailable(snapshotRef.current)) {
        keyEvent.preventDefault();
        void connection.sendCommand(chaseRestartCommand()).catch((error) => console.warn('[tv] chase restart failed', error));
      } else if (isModeRulesKey(keyEvent.key) && modeRulesAvailable(snapshotRef.current)) {
        // How to Play (2026-10-15): the phone opens the game's rules on the TV.
        keyEvent.preventDefault();
        void connection.sendCommand(modeRulesCommand()).catch((error) => console.warn('[tv] how to play failed', error));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  // Set by the lifecycle effect: forget this TV's device and show a new code.
  const forgetDeviceRef = useRef<(() => void) | null>(null);
  const lastControllerSignalRef = useRef(Date.now());
  const lastControllerSequenceRef = useRef(0);
  // THE PERSISTENT COURSE: the last snapshot that carried a putt scene. Once
  // a session has shown a course, `scene: null` means "the phone is between
  // putts": keep the cached green as the backdrop and put the phone's context
  // on top instead of dropping to a blank screen.
  const lastCourseSnapshotRef = useRef<TvSnapshot | null>(null);
  if (snapshot?.scene?.kind === 'putt') lastCourseSnapshotRef.current = snapshot;
  const [awakeNow, setAwakeNow] = useState(() => Date.now());
  const puttAtMs = event?.createdAtMs ?? null;
  useEffect(() => {
    if (puttAtMs == null) return;
    const until = puttAtMs + AWAKE_AFTER_PUTT_MS;
    if (until <= Date.now()) return;
    const timer = window.setTimeout(() => setAwakeNow(Date.now()), until - Date.now() + 50);
    return () => window.clearTimeout(timer);
  }, [puttAtMs]);
  useScreenWakeLock(shouldHoldScreenAwake(phase, snapshot, event, awakeNow));
  const liveNotice = useLiveNotice(demo ? demo.snapshot.notice : snapshot?.notice);

  // Idle-time preload: the stage chunk, then the biome the page will most
  // likely draw (the last course this session, else the page's stock pick
  // that resolveSceneCourse uses when the phone names none). Runs behind the
  // first paint of whatever screen is up.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      void import('./PuttStage')
        .then(() => {
          const shown = demo?.snapshot.scene ?? lastCourseSnapshotRef.current?.scene ?? null;
          const last = shown ? resolveSceneCourse(shown).sceneryId : null;
          return preloadBiome(last ?? pageStockScenery().sceneryId, detectStageGraphics());
        })
        .catch((error) => console.warn('[tv] stage preload failed', error));
    }, 300);
    return () => window.clearTimeout(timer);
    // Once per page: the fixture and the stock pick never change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Session lifecycle: resume with the stored token, otherwise pair; on
  // close/expiry resume again with backoff; never reload the page.
  useEffect(() => {
    if (devDemo) return;
    let disposed = false;
    let unsubscribe: Unsubscribe | null = null;
    let retryTimer: number | null = null;
    let sessionExpiryTimer: number | null = null;
    let pairingExpiryTimer: number | null = null;
    let attempt = 0;

    const clearTimers = () => {
      for (const timer of [retryTimer, sessionExpiryTimer, pairingExpiryTimer]) {
        if (timer != null) window.clearTimeout(timer);
      }
      retryTimer = null;
      sessionExpiryTimer = null;
      pairingExpiryTimer = null;
    };
    const teardown = (reason: string) => {
      clearTimers();
      unsubscribe?.();
      unsubscribe = null;
      const connection = connectionRef.current;
      connectionRef.current = null;
      if (connection) console.warn('[tv] session restart:', reason);
      void connection?.close(reason);
    };
    /** Drop the current session and open another after the backoff. */
    const restart = (delayMs: number, reason: string) => {
      if (disposed) return;
      teardown(reason);
      const stored = loadStoredDevice() !== null;
      setHasStoredDevice(stored);
      setPhase(stored ? 'reconnecting' : 'opening');
      setPairingCode('');
      retryTimer = window.setTimeout(() => void open(), delayMs);
    };
    const restartWithBackoff = (reason: string) => {
      const delayMs = resumeBackoffMs(attempt);
      attempt += 1;
      restart(delayMs, reason);
    };

    const applySession = (session: TvSessionDocument) => {
      const now = Date.now();
      const controllerSeenAtMs = Number(session.lastControllerSeenAtMs);
      if (Number.isFinite(controllerSeenAtMs) && controllerSeenAtMs > 0) {
        lastControllerSignalRef.current = controllerSeenAtMs;
      } else if (session.sequence !== lastControllerSequenceRef.current) {
        // A session created before the controller-freshness field shipped.
        // Receiver heartbeats do not advance the sequence, so they cannot
        // fake a live phone.
        lastControllerSignalRef.current = now;
      }
      lastControllerSequenceRef.current = session.sequence;
      diagState.appliedSeq = typeof session.sequence === 'number' ? session.sequence : null;
      setTvLinkFresh(controllerFresh(lastControllerSignalRef.current, now));

      // The one-time device token (first pairing, or a rotation on resume):
      // store it before anything else can go wrong.
      const issued = deviceFromIssue(session.deviceTokenIssue, loadStoredDevice(), session.receiverName || 'LagKing TV');
      if (issued) {
        storeDevice(issued);
        setHasStoredDevice(true);
      }

      setOwnerHandle(typeof session.ownerHandle === 'string' && session.ownerHandle ? session.ownerHandle : null);
      if (session.status === 'closed' || session.expiresAtMs <= now) {
        restartWithBackoff(session.status === 'closed' ? 'session-closed-remotely' : 'session-expired');
        return;
      }
      // Keep unchanged parts' identity so a HUD-only change does not re-render the 3D stage.
      setSnapshot((previous) => shareStructure(previous, session.snapshot));
      // Sound: the phone's settings, and any new cheer (sfx.ts).
      tvSfx.applySnapshot(session.snapshot?.sound, Date.now());
      // A null event is meaningful: the phone has moved on from the last
      // putt. Clear it here too so no settled ball or result stays frozen.
      setEvent((previous) => shareStructure(previous, session.event?.kind === 'putt' ? session.event : null));
      if (session.status === 'paired' || issued) {
        if (pairingExpiryTimer != null) window.clearTimeout(pairingExpiryTimer);
        pairingExpiryTimer = null;
        setPairingCode('');
        setPhase('live');
      }
    };

    const open = async () => {
      if (disposed) return;
      const device = loadStoredDevice();
      let connection: TvReceiverConnection;
      try {
        connection = device ? await resumeReceiver(device) : await createReceiver();
      } catch (error) {
        if (disposed) return;
        if (device && resumeFailureAction(callableErrorCode(error)) === 'pair') {
          // Revoked or unknown token: this TV was removed from the account.
          // Forget it and show a code.
          clearStoredDevice();
          attempt = 0;
          restart(0, 'resume-refused-repair');
          return;
        }
        restartWithBackoff('open-failed:' + String(callableErrorCode(error) ?? 'unknown'));
        return;
      }
      if (disposed) {
        void connection.close('page-disposed');
        return;
      }
      attempt = 0;
      connectionRef.current = connection;
      lastControllerSignalRef.current = Date.now();
      lastControllerSequenceRef.current = 0;
      if (connection.pairing) {
        setPairingCode(connection.pairing.code);
        setPhase('pairing');
        // Nobody paired before the code expired: a fresh code, no reload.
        pairingExpiryTimer = window.setTimeout(
          () => restart(0, 'pairing-code-expired'),
          Math.max(1000, connection.pairing.expiresAtMs - Date.now() + 500),
        );
      } else {
        setPairingCode('');
        setPhase('live');
      }
      // The 12-hour session TTL: resume with the token rather than showing
      // an error on a wall-mounted display.
      sessionExpiryTimer = window.setTimeout(
        () => restartWithBackoff('session-ttl'),
        Math.max(1000, connection.expiresAtMs - Date.now() + 500),
      );
      unsubscribe = connection.subscribe(applySession, (error) =>
        restartWithBackoff('snapshot-error:' + String((error as { code?: string })?.code ?? (error as Error)?.message ?? 'unknown').slice(0, 40)),
      );
    };

    // "Pair with a different account" (forgetTv.ts): forget the stored
    // device, close the session, drop what the old account showed, and open
    // a fresh pairing session with no delay.
    forgetDeviceRef.current = () => {
      if (disposed) return;
      clearStoredDevice();
      attempt = 0;
      lastCourseSnapshotRef.current = null;
      setOwnerHandle(null);
      setSnapshot(null);
      setEvent(null);
      resetTally();
      restart(0, 'forget-device');
    };

    void open();
    return () => {
      disposed = true;
      forgetDeviceRef.current = null;
      teardown('effect-cleanup');
    };
  }, [devDemo]);

  // Phone freshness: two missed heartbeat windows (100 s) and the page says
  // "Waiting for your phone" over the last frame. It never reloads; a locked
  // phone resumes the instant it publishes again.
  useEffect(() => {
    if (devDemo || phase !== 'live') {
      setTvLinkFresh(!demoStale);
      return;
    }
    const check = () => setTvLinkFresh(controllerFresh(lastControllerSignalRef.current, Date.now()));
    check();
    const timer = window.setInterval(check, 10_000);
    return () => window.clearInterval(timer);
  }, [demoStale, devDemo, phase]);

  if (demoPairing) return <PairingScreen code="483920" />;
  if (demo) {
    return renderPresentation({
      snapshot: demo.snapshot,
      event: demoAddress ? null : demo.event ?? null,
      tvLinkFresh: !demoStale,
      settled: true,
      reconnecting: false,
      cachedCourse: demo.snapshot.scene?.kind === 'putt' ? demo.snapshot : null,
      notice: liveNotice,
      tally: { mode: demo.snapshot.mode ?? null, ids: ['demo'], proximities: [demo.event?.proximityFt ?? 2.4] },
      // A fixture shows the control (with `&stale`) but never forgets anything.
      onForget: () => console.info('[tv] demo: forget this TV (no-op in a fixture)'),
    });
  }

  if (phase === 'pairing') return <PairingScreen code={pairingCode} />;
  if (phase === 'opening' && !hasStoredDevice) return <PairingScreen code="" status="Opening…" />;

  const courseSettled = !event || event.kind !== 'putt' || settledEventId === event.id;
  return renderPresentation({
    snapshot,
    event,
    tvLinkFresh,
    settled: courseSettled,
    reconnecting: phase !== 'live',
    cachedCourse: lastCourseSnapshotRef.current,
    notice: liveNotice,
    tally,
    onForget: showForgetAction({ hasStoredDevice, tvLinkFresh, reconnecting: phase !== 'live' })
      ? () => forgetDeviceRef.current?.()
      : null,
    // The idle start screen always offers the way out on a paired TV, with
    // the account it's linked to (Cole, 2026-09-30: "I need a way to free
    // myself from this screen. linked to wrong account").
    idleForget: hasStoredDevice ? () => forgetDeviceRef.current?.() : null,
    ownerHandle,
  });
}

